import { Box } from "@mui/material";
import * as React from "react";
import { RouteComponentProps } from "react-router";
import { Base } from "ui/Scripts/source/framework/base";
import { ConfirmationDialogResult } from "ui/Scripts/source/models/enums";
import { IInstantMessageChannelItem, InstantMessageChannelItem } from "ui/Scripts/source/models/instantMessage/instantMessageChannelItem";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "source/framework/appUtils";
import { ITypedOption } from "source/models/common/typedOption";
import { IInstantMessagePage } from "source/models/instantMessage/instantMessagePage";
import * as instantMessageChannelService from "source/models/services/instantMessageChannelService";
import { recordLockManager } from "source/models/services/recordLockService";
import { IAppSessionState } from "source/models/store/storeTypes";
import { HeaderMenuItem, NavigationMenu } from "source/components/app/NavigationMenu";
import { UserMenu } from "source/components/app/UserMenu";
import { SettingsButton } from "source/components/framework/Button/SettingsButton";
import { MainListContainer } from "source/components/framework/List/MainListContainer";
import { PageContainer } from "source/components/framework/Page/PageContainer";
import { PageContent } from "source/components/framework/Page/PageContent";
import { PageTitle } from "source/components/framework/Page/PageTitle";
import { PageTopBar } from "source/components/framework/Page/PageTopBar";
import { IPageNavMenuItem } from "source/components/framework/PageNav/PageNavMenuItems";
import { InstantMessageChannelDialogMenuItem, InstantMessageChannelEditDialog } from "../InstantMessageChannelEditDialog";
import { InstantMessageChannelEditNewDialog } from "../InstantMessageChannelEditNewDialog";
import { ChannelInstantMessageList } from "../InstantMessageList/InstantMessageList";
import { InstantMessageSettingsDialog, InstantMessageSettingsDialogMenuItem } from "../InstantMessageSettingsDialog";
import { PageNavMenuItemInstantMessageChannel } from "../PageNavMenuItemInstantMessageChannel";
import { abortManager } from "source/models/services/tokenService";

export interface IInstantMessagePageOwnProps {
}

export interface IInstantMessagePageStateProps {
    appState: IAppSessionState;
    dataId: number;
    instantMessagePage: IInstantMessagePage;
}

export interface IInstantMessagePageDispatchProps {
    onGetInstantMessagePage: () => void;
    onLogout: () => void;
    onSetCulture: (culture: string) => void;
    onSaveUserParameters: (parameters: ITypedOption[]) => void;
    onRefreshSessionState: () => void;
}

export interface IInstantMessagePagePathProps {
    channelId: string;
}

type InstantMessagePageProp = IInstantMessagePageOwnProps & IInstantMessagePageStateProps & IInstantMessagePageDispatchProps & RouteComponentProps<IInstantMessagePagePathProps>;

interface IInstantMessagePageState {
    initialized: boolean;
    showNavPanel: boolean;
    activeCompanyId: string;
    channels: IInstantMessageChannelItem[];
    activeChannelId: string;
    activeChannelHasUnreadMessagesInList: boolean;
    settingsActions: IPageNavMenuItem[];
    // Channel new
    showInstantMessageChannelNewDialog: boolean;
    // Channel edit
    showInstantMessageChannelEditDialog: boolean;
    instantMessageChannelEditDialogChannelId: string;
    instantMessageChannelEditDialogDefaultMenuItem: InstantMessageChannelDialogMenuItem;
    // Properties
    dataId: number;
    showSettingsDialog: boolean;
    hasPartners: boolean;
    filterActive: boolean;
}

export class InstantMessagePage extends React.Component<InstantMessagePageProp, IInstantMessagePageState> {
    getSettingsActions = (): IPageNavMenuItem[] => {
        const props = this.props;
        const sessionState = props.appState.state;
        const result: IPageNavMenuItem[] = [];
        if (sessionState.isCompanyAdmin || sessionState.canManageInstantMessagePermissions) {
            result.push({ id: InstantMessageSettingsDialogMenuItem.Permissions, title: Translations.Permissions }); //, onClick: this.handleOpenPermissions
        }
        if (sessionState.isSuperAdmin) {
            result.push({ id: InstantMessageSettingsDialogMenuItem.InstantMessagePartners, title: Translations.InstantMessagePartners }); //, onClick: this.handleOpenInstantMessagePartners
        }
        //if (this.props.sessionState.isCompanyAdmin) {
        //    result.push({ title: Translations.CompanyInstantMessageChannels, onClick: this.handleOpenCompanyInstantMessageChannels });
        //}
        return result;
    };

    getDefaultChannelId = (props: InstantMessagePageProp): string => {
        const channels = props.instantMessagePage ? props.instantMessagePage.channels : [];
        const activeChannel = channels.length > 0 && props.match.params.channelId ? channels.find(i => i.id === props.match.params.channelId) : null;
        return activeChannel ? activeChannel.id : (channels.length > 0 ? channels[0].id : "");
    };

    constructor(props: InstantMessagePageProp) {
        super(props);
        abortManager.abortAll(); //Abort all abortable calls regardless of module
        const channels = props.instantMessagePage ? props.instantMessagePage.channels : [];
        this.state = {
            initialized: !!props.instantMessagePage,
            showNavPanel: false,
            activeCompanyId: props.appState.state.companyId,
            channels,
            activeChannelId: this.getDefaultChannelId(props),
            activeChannelHasUnreadMessagesInList: false,
            settingsActions: this.getSettingsActions(),
            // Channel new
            showInstantMessageChannelNewDialog: false,
            // Channel edit
            showInstantMessageChannelEditDialog: false,
            instantMessageChannelEditDialogChannelId: "",
            instantMessageChannelEditDialogDefaultMenuItem: InstantMessageChannelDialogMenuItem.General,
            // Properties
            dataId: props.dataId,
            showSettingsDialog: false,
            hasPartners: false,
            filterActive: false
        };
    }

    componentDidMount(): void {
        const sessionState = this.props.appState.state;
        recordLockManager.unlockAll(sessionState?.companyId, sessionState?.code);
        this.props.onGetInstantMessagePage();
        AppUtils.setNewInstantMessages([]);
    }

    componentDidUpdate(prevProps: InstantMessagePageProp, prevState: IInstantMessagePageState): void {
        const props = this.props;
        const state = this.state;
        const prevCompanyIds = (prevProps.instantMessagePage ? prevProps.instantMessagePage.companies.map(i => i.id) : []).join(",");
        const companyIds = (props.instantMessagePage ? props.instantMessagePage.companies.map(i => i.id) : []).join(",");
        const prevCompanyId = prevProps.instantMessagePage && prevProps.instantMessagePage.companies.length > 0 ? prevProps.instantMessagePage.companies[0].id : Base.emptyGuid;
        const companyId = props.instantMessagePage && props.instantMessagePage.companies.length > 0 ? props.instantMessagePage.companies[0].id : Base.emptyGuid;
        const prevParamChanneldId = prevProps.match.params.channelId ?? "";
        const paramChanneldId = props.match.params.channelId ?? "";
        if (prevProps.dataId === props.dataId && prevCompanyId === companyId && prevCompanyIds === companyIds && prevParamChanneldId === paramChanneldId) return;
        const channels = props.instantMessagePage ? props.instantMessagePage.channels : [];
        InstantMessageChannelItem.sortInstantMessageListItems(channels);
        this.setState({
            dataId: props.dataId,
            initialized: !!props.instantMessagePage,
            showNavPanel: state.initialized ? state.showNavPanel : false,
            activeCompanyId: companyId,
            channels,
            activeChannelId: this.getDefaultChannelId(props),
            activeChannelHasUnreadMessagesInList: false,
            settingsActions: this.getSettingsActions(),
        });
    }

    handleToggleShowNavPanel = () => {
        this.setState({ showNavPanel: !this.state.showNavPanel });
    };

    handleChangeActiveCompanyId = async (companyId: string) => {
        if (this.state.activeCompanyId === companyId) return;
        this.refreshChannelList(companyId, this.state.activeChannelId);
    };

    handleSelectChannel = async (id: string) => {
        const state = this.state;
        if (state.activeChannelId === id) return;
        const oldActiveChannel = state.channels.find(i => i.id === state.activeChannelId);
        if (oldActiveChannel) {
            oldActiveChannel.hasUnreadMessages = false;
        }
        this.setState({
            activeChannelId: id,
            activeChannelHasUnreadMessagesInList: false
        });
    };

    handleEditChannel = (id: string) => {
        if (!id) return;
        const channel = this.state.channels.find(i => i.id === id);
        if (!channel) return;
        this.setState({
            showInstantMessageChannelEditDialog: true,
            instantMessageChannelEditDialogChannelId: id,
            instantMessageChannelEditDialogDefaultMenuItem: InstantMessageChannelDialogMenuItem.General
        });
    };

    handleExitChannel = async (id: string) => {
        if (!id) return;
        const state = this.state;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToExitInstantMessageChannel) === ConfirmationDialogResult.Yes) {
            // Call server
            const result = await AppUtils.callService(() => instantMessageChannelService.exitInstantMessageChannel(id, state.activeCompanyId));
            if (!result) return;
            this.refreshChannelList(state.activeCompanyId, "");
        }
    };

    refreshChannelList = async (companyId: string, instantMessageChannelId: string) => {
        const props = this.props;
        const sessionState = props.appState.state;
        const items = companyId === sessionState.companyId
            ? await instantMessageChannelService.getInstantMessageChannelItems(companyId)
            : null;
        const channels = items ? items.channels : [];
        InstantMessageChannelItem.sortInstantMessageListItems(channels);
        if (!instantMessageChannelId) {
            instantMessageChannelId = channels.length > 0 ? channels[0].id : "";
        }
        this.setState({
            activeCompanyId: companyId,
            activeChannelId: instantMessageChannelId,
            channels,
            settingsActions: this.getSettingsActions()
        });
    };

    handleInstantMessageChannelDialogOk = () => {
        const state = this.state;
        this.setState({
            showInstantMessageChannelEditDialog: false
        });
        this.refreshChannelList(state.activeCompanyId, state.activeChannelId);
    };

    handleInstantMessageChannelDialogCancel = (instantMessageChannelDataWasModified: boolean) => {
        const state = this.state;
        this.setState({
            showInstantMessageChannelEditDialog: false
        });
        if (!instantMessageChannelDataWasModified) return;
        this.refreshChannelList(state.activeCompanyId, state.activeChannelId);
    };

    handlenstantMessageChannelDialogNewSaved = (newInstantMessageChannelId: string) => {
        this.setState({
            showInstantMessageChannelEditDialog: true,
            instantMessageChannelEditDialogChannelId: newInstantMessageChannelId,
            instantMessageChannelEditDialogDefaultMenuItem: InstantMessageChannelDialogMenuItem.Users
        });
        this.refreshChannelList(this.state.activeCompanyId, newInstantMessageChannelId);
    };

    handleAddChannel = () => {
        this.setState({
            showInstantMessageChannelNewDialog: true
        });
    };

    handleInstantMessageChannelNewDialogOk = (newInstantMessageChannelId: string) => {
        const state = this.state;
        this.setState({
            showInstantMessageChannelNewDialog: false
        });
        this.refreshChannelList(state.activeCompanyId, newInstantMessageChannelId);
    };

    handleInstantMessageChannelNewDialogCancel = () => {
        this.setState({
            showInstantMessageChannelNewDialog: false
        });
    };

    handleInvitation = async (instantMessageChannelUserId: string, instantMessageChanneld: string, approve: boolean) => {
        const state = this.state;
        const result = await AppUtils.callService(() => instantMessageChannelService.instantMessageChannelUserInvitationHandle(instantMessageChannelUserId, instantMessageChanneld, approve), false);
        if (!result) return;
        this.refreshChannelList(state.activeCompanyId, state.activeChannelId);
    };

    // #region Settings
    handleOpenSettings = async () => {
        this.setState({
            showSettingsDialog: true,
        });
    };

    handleSettingsDialogOk = async (refreshView: boolean) => {
        this.setState({
            showSettingsDialog: false,
            dataId: this.state.dataId + 1
        });
    };
    // #endregion Settings

    // #region SignalR
    handleInstantMessageUpdated = (item: IInstantMessageListItem) => {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        if (!item) return;
        const channels = state.channels.slice(0);
        const channel = channels.find(i => i.id === item.channelId);
        if (!channel) return;
        const currentLatestDate = channel.latestMessage?.modifyUser?.date;
        if (!Base.isNullOrUndefined(currentLatestDate) && currentLatestDate > item.modifyUser.date) return;
        channel.latestMessage = item;
        const modifyUserCode = item?.modifyUser?.code ?? sessionState.code;
        let activeChannelHasUnreadMessagesInList = state.activeChannelHasUnreadMessagesInList;
        if (modifyUserCode !== sessionState.code && !item.isUserRead()) {
            if (item.channelId !== state.activeChannelId) {
                channel.hasUnreadMessages = true;
            } else {
                activeChannelHasUnreadMessagesInList = true;
            }
        }
        InstantMessageChannelItem.sortInstantMessageListItems(channels);
        this.setState({
            channels,
            activeChannelHasUnreadMessagesInList
        });
    };

    handleInstantMessageChannelRemoved = (id: string) => {
        const state = this.state;
        if (!id) return;
        const channels = state.channels.filter(i => i.id !== id);
        this.setState({
            channels
        });
    };

    handleInstantMessageChannelUpdated = (item: IInstantMessageChannelItem) => {
        const state = this.state;
        if (!item) return;
        const channels = state.channels.filter(i => i.id !== item.id);
        channels.push(item);
        InstantMessageChannelItem.sortInstantMessageListItems(channels);
        this.setState({
            channels
        });
    };

    handleInstantMessageChannelSetHasUnreadMessagesOnList = (value: boolean) => {
        this.setState({
            activeChannelHasUnreadMessagesInList: value
        });
    };
    // #endregion SignalR

    render() {
        const props = this.props;
        const state = this.state;
        const sessionState = props.appState.state;
        const companies = props.instantMessagePage ? props.instantMessagePage.companies : [];
        const showContent = state.activeCompanyId === sessionState.companyId;
        const channel = state.channels.find(i => i.id === state.activeChannelId);
        return (
            <PageContainer>
                <NavigationMenu
                    appState={props.appState}
                    activeMenuItem={HeaderMenuItem.InstantMessages}
                    companies={companies}
                    activeCompanyId={state.activeCompanyId}
                    onChangeActiveCompany={this.handleChangeActiveCompanyId}
                />
                <PageContent>
                    <PageTopBar>
                        <UserMenu
                            appState={props.appState}
                            onLogout={props.onLogout}
                            onSetCulture={props.onSetCulture}
                            onRefreshSessionState={props.onRefreshSessionState}
                        />
                    </PageTopBar>
                    <Box sx={{ display: "flex" }}>
                        <Box sx={{ width: "400px", position: "relative" }}>
                            <PageNavMenuItemInstantMessageChannel
                                isActive={true}
                                hidden={!showContent}
                                firstLine={companies.length < 2}
                                channels={state.channels}
                                activeChannelId={state.activeChannelId}
                                activeChannelHasUnreadMessagesInList={state.activeChannelHasUnreadMessagesInList}
                                userCode={sessionState.code}
                                onAddChannel={this.handleAddChannel}
                                onToggleMenuItem={null}
                                onChangeActiveChannel={this.handleSelectChannel}
                                onEditChannel={this.handleEditChannel}
                                onHandleInvitation={this.handleInvitation}
                            />
                        </Box>
                        <Box sx={{ position: "relative", width: "calc(100% - 400px)" }}>
                            <PageTitle position="absolute" title={(channel ? channel.code : "") + (state.filterActive ? " - " + Translations.Filtered : "")}>
                                {state.settingsActions.length > 0 &&
                                    <SettingsButton onClick={this.handleOpenSettings}>
                                        {Translations.Settings}
                                    </SettingsButton>
                                }
                            </PageTitle>
                            <MainListContainer sx={{ display: "flex", flexDirection: "column", height: "calc(100vh - 160px)" }}>
                                {showContent &&
                                    <ChannelInstantMessageList
                                        sessionState={sessionState}
                                        maxInstantMessageAttachmentSize={props.instantMessagePage?.maxInstantMessageAttachmentSize ?? 0}
                                        companyId={state.activeCompanyId}
                                        userCode={sessionState.code}
                                        channel={state.channels.find(i => i.id === state.activeChannelId)}
                                        onFilterActiveChanged={filterActive => this.setState({ filterActive })}
                                        onEditChannel={this.handleEditChannel}
                                        onExitChannel={this.handleExitChannel}
                                        onInstantMessageUpdated={this.handleInstantMessageUpdated}
                                        onInstantMessageChannelRemoved={this.handleInstantMessageChannelRemoved}
                                        onInstantMessageChannelUpdated={this.handleInstantMessageChannelUpdated}
                                        onInstantMessageChannelSetHasUnreadMessagesOnList={this.handleInstantMessageChannelSetHasUnreadMessagesOnList}
                                    />
                                }
                            </MainListContainer>
                        </Box>
                    </Box>
                </PageContent>
                {state.showInstantMessageChannelNewDialog &&
                    <InstantMessageChannelEditNewDialog
                        sessionState={sessionState}
                        companyId={state.activeCompanyId}
                        onOk={this.handleInstantMessageChannelNewDialogOk}
                        onCancel={this.handleInstantMessageChannelNewDialogCancel}
                    />
                }
                {state.showInstantMessageChannelEditDialog &&
                    <InstantMessageChannelEditDialog
                        sessionState={sessionState}
                        defaultMenuItem={state.instantMessageChannelEditDialogDefaultMenuItem}
                        companyId={state.activeCompanyId}
                        instantMessageChannelId={state.instantMessageChannelEditDialogChannelId}
                        onOk={this.handleInstantMessageChannelDialogOk}
                        onCancel={this.handleInstantMessageChannelDialogCancel}
                        refreshChannelList={this.refreshChannelList}
                        onNewSaved={this.handlenstantMessageChannelDialogNewSaved}
                    />
                }
                {state.showSettingsDialog &&
                    <InstantMessageSettingsDialog
                        companyId={state.activeCompanyId}
                        onClose={this.handleSettingsDialogOk}
                        settingsActions={state.settingsActions}
                        sessionState={sessionState}
                    />
                }
            </PageContainer>
        );
    }
}